import { useCallback } from "react";
import { Location, TripTransport } from "@/types";
import { DateTime } from "luxon";
import { generateDaysArray } from "@/utils/tripBuilder";
import useAccommodationStore from "@/stores/tripBuilder/useAccommodation";
import useTrip from "@/stores/tripBuilder/useTrip";
import useView from "@/stores/tripBuilder/useView";
import useTransport from "@/stores/tripBuilder/useTransport";
import useExperiences from "@/stores/tripBuilder/useExperiences";

const useLocationManager = () => {
  // Trip Store
  const tripData = useTrip((state) => state.tripData);
  const updateTripData = useTrip((state) => state.updateTripData);
  const setSelectedLocation = useTrip((state) => state.setSelectedLocation);
  const setSelectedDay = useTrip((state) => state.setSelectedDay);
  const getAdjacentLocations = useTrip((state) => state.getAdjacentLocations);
  const setPreviousLocation = useTrip((state) => state.setPreviousLocation);
  const setNextLocation = useTrip((state) => state.setNextLocation);
  const setSelectedLocationBudget = useTrip(
    (state) => state.setSelectedLocationBudget
  );

  // View Store
  const setPanel = useView((state) => state.setPanel);

  // Transport Store
  const setActiveTransportButtonId = useTransport(
    (state) => state.setActiveTransportButtonId
  );
  const setTransportData = useTransport((state) => state.setTransportData);
  const setOverViewMapViewport = useTransport(
    (state) => state.setOverViewMapViewport
  );
  const setAccommodationMapViewport = useTransport(
    (state) => state.setAccommodationMapViewport
  );
  const setAccommodationBounds = useTransport(
    (state) => state.setAccommodationBounds
  );

  // Experiences Store
  const isModalOpen = useExperiences((state) => state.isModalOpen);
  const setShouldCloseModal = useExperiences(
    (state) => state.setShouldCloseModal
  );

  const updateIndices = useCallback((locations: Location[]) => {
    return locations.map((loc, index) => ({ ...loc, index }));
  }, []);

  const handleLocationClick = useCallback(
    (location: Location) => {
      if (isModalOpen) {
        setShouldCloseModal(true);
      }
      setSelectedLocation(location);
      setSelectedDay(location.date.start_date);
      setSelectedLocationBudget(location.budget);
      setActiveTransportButtonId(null);
      useAccommodationStore.setState({
        lodgings: location?.accommodation.temp || [],
      });

      setOverViewMapViewport({
        longitude: location.coordinates.longitude,
        latitude: location.coordinates.latitude,
        zoom: 10.5,
      });

      setAccommodationMapViewport({
        longitude: location.coordinates.longitude,
        latitude: location.coordinates.latitude,
        zoom: 13,
      });

      const accommodationBounds = boundingBox(
        location.coordinates.latitude,
        location.coordinates.longitude
      );
      setAccommodationBounds(accommodationBounds);

      if (!location.origin) {
        const { previousLocation, nextLocation } = getAdjacentLocations();

        setTransportData({
          selectedLocation: location,
          locationAbove: previousLocation as Location,
          locationBelow: nextLocation as Location,
        });

        setPreviousLocation(previousLocation);
        setNextLocation(nextLocation);
      }

      setPanel("locations");
    },
    [
      setPanel,
      setActiveTransportButtonId,
      setSelectedLocation,
      setSelectedDay,
      setTransportData,
      setOverViewMapViewport,
      setAccommodationMapViewport,
      getAdjacentLocations,
      setPreviousLocation,
      setNextLocation,
      isModalOpen,
      setShouldCloseModal,
      setAccommodationBounds,
      setSelectedLocationBudget,
    ]
  );

  const removeLocationById = useCallback(
    (id: string) => {
      if (!tripData) return;
      const deletedIndex = tripData.locations.findIndex((loc) => loc.id === id);
      if (deletedIndex === -1) return;

      const deletedLocation = tripData.locations[deletedIndex];
      const newLocations = tripData.locations.filter((loc) => loc.id !== id);

      if (!deletedLocation) {
        console.error("Location not found:", deletedLocation);
        return;
      }

      // Remove associated flights
      const newFlights = tripData.transport.filter(
        (flight: TripTransport) =>
          flight.fromLocationId !== id && flight.toLocationId !== id
      );

      // Shift dates for remaining locations
      let currentDate = DateTime.fromISO(deletedLocation.date.start_date, {
        zone: "utc",
      });
      for (let i = deletedIndex; i < newLocations.length; i++) {
        const location = newLocations[i];
        if (!location) {
          console.error("Location date not found:", location);
          continue;
        }
        location.date.start_date = currentDate.toUTC().toISO()!;
        currentDate = currentDate.plus({ days: location.date.num_days });
        location.date.end_date = currentDate
          .minus({ days: 1 })
          .toUTC()
          .toISO() as string;
        location.itinerary_items = generateDaysArray(
          location.date.start_date,
          location.date.end_date
        );
      }

      const updatedLocations = updateIndices(newLocations);

      updateTripData({
        ...tripData,
        locations: updatedLocations,
        transport: newFlights,
      });

      let newSelectedIndex = Math.max(0, deletedIndex - 1);

      if (updatedLocations.length > 0) {
        if (newSelectedIndex === 0 && updatedLocations.length > 1) {
          newSelectedIndex = Math.min(
            deletedIndex,
            updatedLocations.length - 1
          );
        }
        const newSelectedLocation = updatedLocations[newSelectedIndex];
        handleLocationClick(newSelectedLocation!);
      } else {
        if (!tripData.locations[0]) return;
        handleLocationClick(tripData.locations[0]);
      }
    },
    [tripData, updateTripData, updateIndices, handleLocationClick]
  );

  return {
    locations: tripData?.locations || [],
    removeLocationById,
    handleLocationClick,
  };
};

const boundingBox = (lat: number, lon: number, distanceKm: number = 3) => {
  const earthRadius = 6371.0;

  const latRad = degreesToRadians(lat);
  const lonRad = degreesToRadians(lon);

  const angularDistance = distanceKm / earthRadius;

  const minLatRad = latRad - angularDistance;
  const maxLatRad = latRad + angularDistance;

  const deltaLon = Math.asin(Math.sin(angularDistance) / Math.cos(latRad));
  const minLonRad = lonRad - deltaLon;
  const maxLonRad = lonRad + deltaLon;

  const minLat = radiansToDegrees(minLatRad);
  const maxLat = radiansToDegrees(maxLatRad);
  const minLon = radiansToDegrees(minLonRad);
  const maxLon = radiansToDegrees(maxLonRad);

  return {
    _sw: { lng: minLon, lat: minLat },
    _ne: { lng: maxLon, lat: maxLat },
  };
};

const degreesToRadians = (degrees: number): number => {
  return degrees * (Math.PI / 180);
};

const radiansToDegrees = (radians: number): number => {
  return radians * (180 / Math.PI);
};

export default useLocationManager;
